import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateLocalesOptions,
  BulkCreateLocalesResponse,
  BulkDeleteLocalesResponse,
  BulkUpdateLocalesOptions,
  BulkUpdateLocalesResponse,
  CreateNewPrimaryLocaleApplicationErrors,
  CreateNewPrimaryLocaleResponse,
  GetNewPrimaryLocaleStatusResponse,
  ListSupportedLocalesOptions,
  ListSupportedLocalesResponse,
  LocaleCreatedEnvelope,
  LocaleDeletedEnvelope,
  LocaleMultilingualPrimaryLocaleChangedEnvelope,
  LocaleUpdatedEnvelope,
  LocalesQueryBuilder,
  MaskedLocale,
  QueryLocalesOptions,
  QueryLocalesResponse,
  SetVisitorPrimaryLocaleApplicationErrors,
  SetVisitorPrimaryLocaleResponse,
  UpdateLocale,
  V2Locale,
  V2LocaleQuery,
  bulkCreateLocales as universalBulkCreateLocales,
  bulkDeleteLocales as universalBulkDeleteLocales,
  bulkUpdateLocales as universalBulkUpdateLocales,
  createLocale as universalCreateLocale,
  createNewPrimaryLocale as universalCreateNewPrimaryLocale,
  deleteLocale as universalDeleteLocale,
  getLocale as universalGetLocale,
  getNewPrimaryLocaleStatus as universalGetNewPrimaryLocaleStatus,
  listSupportedLocales as universalListSupportedLocales,
  queryLocales as universalQueryLocales,
  setVisitorPrimaryLocale as universalSetVisitorPrimaryLocale,
  typedQueryLocales as universalTypedQueryLocales,
  updateLocale as universalUpdateLocale,
} from './multilingual-locales-v2-locale-locales.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/multilingual' };

export function listSupportedLocales(
  httpClient: HttpClient
): ListSupportedLocalesSignature {
  return (options?: ListSupportedLocalesOptions) =>
    universalListSupportedLocales(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListSupportedLocalesSignature {
  /**
   * Retrieves a list of all Wix-supported locales that can be added to a site.
   *
   * Call this method to display supported language details when creating or updating a locale.
   * @returns Response message for listing supported locales
   */
  (options?: ListSupportedLocalesOptions): Promise<
    NonNullablePaths<ListSupportedLocalesResponse, `supportedLocales`, 2>
  >;
}

export function createLocale(httpClient: HttpClient): CreateLocaleSignature {
  return (locale: NonNullablePaths<V2Locale, `languageCode`, 2>) =>
    universalCreateLocale(
      locale,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateLocaleSignature {
  /**
   * Creates a new secondary locale.
   *
   * This method adds a new secondary locale to the site. A site can have up to 200 locales and up to 100 visible locales.
   *
   * To retrieve a list of all locales supported by Wix that can be created on a site, call List Supported Locales.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * - This method can only be called on sites where multilingual mode is enabled.
   * - This method creates only secondary locales. To create and set a new primary locale, call Create New Primary Locale.
   *
   * </blockquote>
   * @param - Locale to create.
   * @returns The created locale.
   */
  (locale: NonNullablePaths<V2Locale, `languageCode`, 2>): Promise<
    NonNullablePaths<
      V2Locale,
      | `visibility`
      | `effectiveUrlStructure`
      | `urlStructureOverride.urlStructure`,
      3
    >
  >;
}

export function getLocale(httpClient: HttpClient): GetLocaleSignature {
  return (localeId: string) =>
    universalGetLocale(
      localeId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLocaleSignature {
  /**
   * Retrieves a locale by ID.
   * @param - ID of the locale to retrieve.
   * @returns The requested locale.
   */
  (localeId: string): Promise<
    NonNullablePaths<
      V2Locale,
      | `visibility`
      | `effectiveUrlStructure`
      | `urlStructureOverride.urlStructure`,
      3
    >
  >;
}

export function updateLocale(httpClient: HttpClient): UpdateLocaleSignature {
  return (_id: string, locale: NonNullablePaths<UpdateLocale, `revision`, 2>) =>
    universalUpdateLocale(
      _id,
      locale,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLocaleSignature {
  /**
   * Updates a locale by ID.
   * @param - Locale ID.
   *
   * The locale ID is made up of the language and region codes supported by Wix. For example, `"en-US"` for U.S. English.
   * @returns The updated locale.
   */
  (_id: string, locale: NonNullablePaths<UpdateLocale, `revision`, 2>): Promise<
    NonNullablePaths<
      V2Locale,
      | `visibility`
      | `effectiveUrlStructure`
      | `urlStructureOverride.urlStructure`,
      3
    >
  >;
}

export function deleteLocale(httpClient: HttpClient): DeleteLocaleSignature {
  return (localeId: string) =>
    universalDeleteLocale(
      localeId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteLocaleSignature {
  /**
   * Deletes a locale by ID.
   *
   * This method removes only secondary locales from a site. You can't call this method to delete the primary locale or a locale that's set as the visitor primary locale.
   *
   * <blockquote class="warning">
   * <strong>Warning:</strong>
   *
   * Deleting a locale permanently removes it and all its associated data from the site.
   *
   * </blockquote>
   *
   * <blockquote class="tip">
   * <strong>Tip:</strong>
   *
   * - To change the primary locale, call Create New Primary Locale. This method automatically deletes the original primary locale and transfers its data to the new primary locale.
   * - To delete a locale that's set as the visitor primary locale, first call Set Visitor Primary Locale to change the visitor primary locale, then call this method.
   *
   * </blockquote>
   * @param - ID of the locale to delete.
   * @returns Response message for deleting a Locale.
   */
  (localeId: string): Promise<void>;
}

export function queryLocales(httpClient: HttpClient): QueryLocalesSignature {
  return (options?: QueryLocalesOptions) =>
    universalQueryLocales(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLocalesSignature {
  /**
   * Retrieves a list of the site's locales, given the provided paging, filtering, and sorting. Up to 200 locales can be returned per request.
   *
   * The default `sort` is `locale.id` in `ASC`.
   *
   * To learn how to query projects, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language).
   */
  (options?: QueryLocalesOptions): LocalesQueryBuilder;
}

export function typedQueryLocales(
  httpClient: HttpClient
): TypedQueryLocalesSignature {
  return (query: V2LocaleQuery, options?: QueryLocalesOptions) =>
    universalTypedQueryLocales(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLocalesSignature {
  /** @returns Response message for querying Locales. */
  (query: V2LocaleQuery, options?: QueryLocalesOptions): Promise<
    NonNullablePaths<
      QueryLocalesResponse,
      | `locales`
      | `locales.${number}.visibility`
      | `locales.${number}.effectiveUrlStructure`
      | `locales.${number}.urlStructureOverride.urlStructure`,
      5
    >
  >;
}

export function bulkCreateLocales(
  httpClient: HttpClient
): BulkCreateLocalesSignature {
  return (
    locales: NonNullablePaths<V2Locale, `languageCode`, 2>[],
    options?: BulkCreateLocalesOptions
  ) =>
    universalBulkCreateLocales(
      locales,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateLocalesSignature {
  /**
   * Creates multiple secondary locales.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * - This method can only be called on sites where multilingual mode is enabled.
   *
   * </blockquote>
   * @param - Locales to create.
   * @returns Response message for bulk creating Locales.
   */
  (
    locales: NonNullablePaths<V2Locale, `languageCode`, 2>[],
    options?: BulkCreateLocalesOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateLocalesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.visibility`
      | `results.${number}.item.effectiveUrlStructure`
      | `results.${number}.item.urlStructureOverride.urlStructure`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateLocales(
  httpClient: HttpClient
): BulkUpdateLocalesSignature {
  return (
    locales: NonNullablePaths<
      MaskedLocale,
      `locale._id` | `locale.revision`,
      3
    >[],
    options?: BulkUpdateLocalesOptions
  ) =>
    universalBulkUpdateLocales(
      locales,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateLocalesSignature {
  /**
   * Updates multiple locales.
   * @param - Locales to update.
   * @returns Response message for bulk updating Locales.
   */
  (
    locales: NonNullablePaths<
      MaskedLocale,
      `locale._id` | `locale.revision`,
      3
    >[],
    options?: BulkUpdateLocalesOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateLocalesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.visibility`
      | `results.${number}.item.effectiveUrlStructure`
      | `results.${number}.item.urlStructureOverride.urlStructure`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteLocales(
  httpClient: HttpClient
): BulkDeleteLocalesSignature {
  return (localeIds: string[]) =>
    universalBulkDeleteLocales(
      localeIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteLocalesSignature {
  /**
   * Deletes multiple locales.
   *
   * This method removes only secondary locals from a site. The primary locale can't be deleted.
   *
   * <blockquote class="warning">
   * <strong>Warning:</strong>
   *
   * This method permanently deletes multiple secondary locales and their data from the site.
   * </blockquote>
   * @param - IDs of locales to delete.
   * @returns Response message for bulk deleting Locales.
   */
  (localeIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteLocalesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function createNewPrimaryLocale(
  httpClient: HttpClient
): CreateNewPrimaryLocaleSignature {
  return (primaryLocale: V2Locale) =>
    universalCreateNewPrimaryLocale(
      primaryLocale,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateNewPrimaryLocaleSignature {
  /**
   * Creates and assigns a new primary locale for a site.
   *
   * This method changes the site's primary locale. The process may take time, depending on the amount of content present on the site. When called, this method returns a token, which you can use with the Get New Primary Locale Status method to check the status of the primary locale change.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * - The desired new primary locale can't already exist as a secondary locale on the site. If the locale is already a secondary locale on the site, you must first remove it from the site by calling Delete Locale before changing the primary locale.
   * - This method automatically deletes the original primary locale and transfers its data to the new primary locale.
   *
   * </blockquote>
   * @param - New primary locale.
   * @returns Response message for changing the main locale.
   */
  (primaryLocale: V2Locale): Promise<
    NonNullablePaths<CreateNewPrimaryLocaleResponse, `token`, 2> & {
      __applicationErrorsType?: CreateNewPrimaryLocaleApplicationErrors;
    }
  >;
}

export function getNewPrimaryLocaleStatus(
  httpClient: HttpClient
): GetNewPrimaryLocaleStatusSignature {
  return (token: string) =>
    universalGetNewPrimaryLocaleStatus(
      token,
      // @ts-ignore
      { httpClient }
    );
}

interface GetNewPrimaryLocaleStatusSignature {
  /**
   * Retrieves the status of the Create New Primary Locale method.
   *
   * Call this method to see if your primary locale was successfully changed. Specify the token returned by Create New Primary Locale. If the token specified is invalid, a 404 error is returned.
   * @param - Token to retrieve the process status. Returned in the response of Create New Primary Locale.
   * @returns Response message for getting the status of the change main locale process.
   */
  (token: string): Promise<
    NonNullablePaths<
      GetNewPrimaryLocaleStatusResponse,
      | `finishSuccessfulData.newPrimaryLocale.visibility`
      | `finishSuccessfulData.newPrimaryLocale.effectiveUrlStructure`
      | `finishSuccessfulData.newPrimaryLocale.urlStructureOverride.urlStructure`
      | `finishWithErrorData.error.code`
      | `finishWithErrorData.error.description`
      | `status`,
      5
    >
  >;
}

export function setVisitorPrimaryLocale(
  httpClient: HttpClient
): SetVisitorPrimaryLocaleSignature {
  return (localeId: string) =>
    universalSetVisitorPrimaryLocale(
      localeId,
      // @ts-ignore
      { httpClient }
    );
}

interface SetVisitorPrimaryLocaleSignature {
  /**
   * Sets a locale as the visitor primary locale for the site.
   *
   * Only one locale can be set as the visitor primary locale at a time. When you update the visitor primary locale, site visitors see the new default language on their next page load.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * The specified locale's `visibility` must be set to `"VISIBLE"`.
   *
   * </blockquote>
   * @param - ID of the locale to set as visitor primary.
   * @returns Response message for setting a visitor primary locale.
   */
  (localeId: string): Promise<
    NonNullablePaths<
      SetVisitorPrimaryLocaleResponse,
      | `locale.visibility`
      | `locale.effectiveUrlStructure`
      | `locale.urlStructureOverride.urlStructure`,
      4
    > & {
      __applicationErrorsType?: SetVisitorPrimaryLocaleApplicationErrors;
    }
  >;
}

export const onLocaleCreated = EventDefinition(
  'wix.multilingual.locales.v2.locale_created',
  true,
  (event: LocaleCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleCreatedEnvelope>();
export const onLocaleDeleted = EventDefinition(
  'wix.multilingual.locales.v2.locale_deleted',
  true,
  (event: LocaleDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleDeletedEnvelope>();
export const onLocaleMultilingualPrimaryLocaleChanged = EventDefinition(
  'wix.multilingual.locales.v2.locale_multilingual_primary_locale_changed',
  true,
  (event: LocaleMultilingualPrimaryLocaleChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.primaryLocale.createdDate' },
            { path: 'data.primaryLocale.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleMultilingualPrimaryLocaleChangedEnvelope>();
export const onLocaleUpdated = EventDefinition(
  'wix.multilingual.locales.v2.locale_updated',
  true,
  (event: LocaleUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  App,
  ApplicationError,
  Asset,
  BaseEventMetadata,
  BillingSubscriptionCancelled,
  BulkActionMetadata,
  BulkCreateLocalesOptions,
  BulkCreateLocalesRequest,
  BulkCreateLocalesResponse,
  BulkDeleteLocalesRequest,
  BulkDeleteLocalesResponse,
  BulkDeleteLocalesResponseBulkLocaleResult,
  BulkLocaleResult,
  BulkUpdateLocalesOptions,
  BulkUpdateLocalesRequest,
  BulkUpdateLocalesResponse,
  BulkUpdateLocalesResponseBulkLocaleResult,
  BusinessSchedule,
  Categories,
  ChangeMainLocaleRequest,
  ChangeMainLocaleResponse,
  ConnectedToSite,
  ConnectionLost,
  ConnectionVerified,
  ConsentPolicy,
  ContactVerificationStatus,
  ContactVerificationStatusChanged,
  CreateLocaleRequest,
  CreateLocaleResponse,
  CreateNewPrimaryLocalePayload,
  CreateNewPrimaryLocaleRequest,
  CreateNewPrimaryLocaleResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DayOfWeek,
  DeleteContext,
  DeleteLocaleRequest,
  DeleteLocaleResponse,
  DeleteStatus,
  DisconnectedFromSite,
  DnssecChanged,
  DomainChangedEvent,
  DomainChangedEventPayloadOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExpirationDateChanged,
  Expired,
  File,
  FinishSuccessfulData,
  FinishWithErrorData,
  ForceDeleteLocaleRequest,
  ForceDeleteLocaleResponse,
  GeoCoordinates,
  GetLocaleRequest,
  GetLocaleResponse,
  GetNewPrimaryLocaleStatusRequest,
  GetNewPrimaryLocaleStatusResponse,
  GetNewPrimaryLocaleStatusResponseDataOneOf,
  GraceEnded,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  ListSupportedLocalesOptions,
  ListSupportedLocalesRequest,
  ListSupportedLocalesResponse,
  Locale,
  LocaleCreatedEnvelope,
  LocaleDeletedEnvelope,
  LocaleMultilingualPrimaryLocaleChangedEnvelope,
  LocaleUpdatedEnvelope,
  LocaleVisibility,
  LocalesQueryBuilder,
  LocalesQueryResult,
  ManagementTypeChanged,
  MaskedLocale,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Multilingual,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  OwnerChanged,
  Page,
  Pages,
  PicassoAssigned,
  PicassoUnassigned,
  PlacementType,
  PriceIncreaseProcessed,
  PrivacyChanged,
  PropagationStarted,
  Properties,
  QueryLocalesOptions,
  QueryLocalesRequest,
  QueryLocalesResponse,
  RawCreateLocaleRequest,
  RawCreateLocaleResponse,
  RedemptionEnded,
  RegionOptions,
  Registered,
  RegistrationCancelled,
  RegistrationExtended,
  ResolutionMethod,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SetVisitorPrimaryLocaleRequest,
  SetVisitorPrimaryLocaleResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePropertiesEvent,
  SitePropertiesUpdated,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  SpecialHourPeriod,
  State,
  Status,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  SupportedLanguage,
  SupportedLocale,
  TimePeriod,
  URI,
  URIs,
  UpdateLocale,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  UrlStructure,
  UrlStructureOverride,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  V2Locale,
  V2LocaleQuerySpec,
  VerificationStarted,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './multilingual-locales-v2-locale-locales.universal.js';
